import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link, Title } from 'react-admin';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { Tile, TileGrid } from '../dashboard/Tile';
import { menuLabel } from '../menuConfig';
import { FeaturesLoading, useFeature, useFeatureState } from '../featureFlags/FeatureGate';
import { TeamLeaderBalance } from './TeamLeaderBalance';
import { useHasPermission } from '../PermissionGate';
import type { PermissionKey } from '../permissions';
import { fmtDateTimeShort } from '../../shared/listCells';
import { MOCKUP_CLASS } from '../../mockupLook/tokens';
import {
  FEATURE_PLANNER_DASHBOARD,
  FEATURE_TEAM_LEADER_BALANCE,
  FEATURE_TEAM_LEADER_DASHBOARD,
  GROUP_TITLES,
  PLANNER_DASHBOARD_RESOURCE,
  PLANNER_GROUP_TITLES,
  TEAM_LEADER_DASHBOARD_RESOURCE,
  TILE_TEXT,
  fetchPlannerDashboard,
  fetchTeamLeaderDashboard,
  groupTiles,
  horizonText,
  tileLink,
  type DashboardTile,
  type PlannerDashboardData,
  type TeamLeaderDashboardData,
} from './ftlDashboardsApi';

/**
 * Pulpity FTL — planisty i Team Leadera (makieta back office v2, fala A, A4;
 * przełączniki `plannerDashboard` i `teamLeaderDashboard`, 20.10.2026).
 * Liczby, od których zaczyna się dzień pracy; każda prowadzi do listy
 * zleceń, harmonogramu floty albo strony powiadomień z filtrem, którym API
 * ją policzyło (kafelek bierze filtr z odpowiedzi, nie składa go sam).
 * Kafelek funkcji wyłączonej nie przychodzi wcale.
 *
 * Planista FTL zaczyna od swojego pulpitu (`RoleDashboard`), pulpit ogólny
 * jest pod odnośnikiem „Pulpit ogólny”. Planista główny (Team Leader) zaczyna
 * od pulpitu ogólnego, a pulpit Team Leadera otwiera z menu, jak
 * administratorzy (decyzja użytkownika 20.10.2026).
 */

export const PLANNER_FEATURE_OFF_NOTE =
  'Pulpit planisty jest wyłączony w wersji aplikacji, z której korzystasz.';
export const PLANNER_NO_PERMISSION_NOTE = 'Pulpit planisty jest dla planowania FTL.';
export const TEAM_LEADER_FEATURE_OFF_NOTE =
  'Pulpit Team Leadera jest wyłączony w wersji aplikacji, z której korzystasz.';
export const TEAM_LEADER_NO_PERMISSION_NOTE =
  'Pulpit Team Leadera jest dla Team Leadera i administratorów.';
export const LOAD_ERROR = 'Nie udało się wczytać pulpitu. Odśwież stronę.';
export const NO_CARETAKER_NOTE =
  'Nie wskazano, czyim jesteś opiekunem FTL — pulpit nie policzy Twoich pojazdów ani regionu. Wskaż to w menu użytkownika: „Jestem opiekunem FTL”.';
export const WORKING_VALUES_NOTE =
  'Region i horyzont to wartości robocze z „Parametrów rekomendacji”, do potwierdzenia przez Marathon.';
export const HORIZON_WORKING_VALUE_NOTE =
  'Horyzont to wartość robocza z „Parametrów rekomendacji”, do potwierdzenia przez Marathon.';

/** Ekran za przełącznikiem i uprawnieniem — inaczej komunikat, jak pulpit Trade. */
function Gate({
  feature,
  permission,
  resource,
  offNote,
  noPermissionNote,
  children,
}: {
  feature: string;
  permission: PermissionKey;
  resource: string;
  offNote: string;
  noPermissionNote: string;
  children: ReactNode;
}) {
  const on = useFeatureState(feature);
  const allowed = useHasPermission(permission);
  if (on === undefined) return <FeaturesLoading />;
  if (!on || !allowed) {
    return (
      <Box sx={{ p: 2 }}>
        <Title title={menuLabel(resource)} />
        <Alert severity="info">{on ? noPermissionNote : offNote}</Alert>
      </Box>
    );
  }
  return <>{children}</>;
}

/** Wczytanie pulpitu z ponowieniem przyciskiem „Odśwież”. */
function useDashboard<T>(load: () => Promise<T>) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let active = true;
    load()
      .then((d) => {
        if (active) {
          setData(d);
          setError(null);
        }
      })
      .catch(() => {
        if (active) setError(LOAD_ERROR);
      });
    return () => {
      active = false;
    };
  }, [load, tick]);
  return { data, error, refresh: () => setTick((n) => n + 1) };
}

/** Sekcje kafelków w kolejności z API. */
function TileGroups({
  tiles,
  titles,
  loading,
}: {
  tiles: readonly DashboardTile[];
  titles: Record<string, string>;
  loading: boolean;
}) {
  if (loading) {
    return (
      <TileGrid>
        <Tile label="Wczytuję…" loading />
      </TileGrid>
    );
  }
  return (
    <>
      {groupTiles(tiles).map(([group, list]) => (
        <Box key={group} data-testid={`ftl-dashboard-group-${group}`}>
          <Typography variant="h6" gutterBottom className={MOCKUP_CLASS.sectionTitle}>
            {titles[group] ?? group}
          </Typography>
          <TileGrid>
            {list.map((t) => {
              const text = TILE_TEXT[t.key] ?? { label: t.key, description: '' };
              return (
                <Box key={t.key} data-testid={`ftl-tile-${t.key}`} data-count={t.count}>
                  <Tile
                    label={text.label}
                    value={t.count}
                    description={text.description}
                    linkTo={tileLink(t)}
                    highlight={t.count > 0 ? text.highlight : undefined}
                  />
                </Box>
              );
            })}
          </TileGrid>
        </Box>
      ))}
    </>
  );
}

/** Stopka: godzina przeliczenia, zgodność z listami, odnośnik do pulpitu ogólnego. */
function Footer({ computedAt, onRefresh }: { computedAt: string; onRefresh: () => void }) {
  return (
    <Stack direction="row" spacing={2} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
      <Typography variant="caption" color="text.secondary" data-testid="ftl-dashboard-computed-at">
        Stan na {fmtDateTimeShort(computedAt)} — każda liczba to ten sam filtr co lista, do której prowadzi.
      </Typography>
      <Button size="small" onClick={onRefresh}>
        Odśwież
      </Button>
      <Link to="/?widok=ogolny">Pulpit ogólny</Link>
    </Stack>
  );
}

export const PlannerDashboard = () => (
  <Gate
    feature={FEATURE_PLANNER_DASHBOARD}
    permission="FTL_PLANNING"
    resource={PLANNER_DASHBOARD_RESOURCE}
    offNote={PLANNER_FEATURE_OFF_NOTE}
    noPermissionNote={PLANNER_NO_PERMISSION_NOTE}
  >
    <PlannerDashboardScreen />
  </Gate>
);

/** „kraj”, „kraj i pierwsza cyfra kodu”, „kraj i 2 pierwsze cyfry kodu”. */
function digitsLevel(digits: number): string {
  return digits === 0
    ? 'kraj'
    : digits === 1
      ? 'kraj i pierwsza cyfra kodu'
      : `kraj i ${digits} pierwsze cyfry kodu`;
}

/**
 * Poziom regionu słowami. Z regionem per kraj (21.10.2026,
 * `regionDigitsByCountry`) — wartość domyślna i kraje z inną liczbą cyfr,
 * pogrupowane: „kraj i 2 pierwsze cyfry kodu; BE, CH, DK, NL — kraj
 * i pierwsza cyfra kodu”.
 */
export function regionLevelText(digits: number, byCountry?: Record<string, number>): string {
  const groups: Record<number, string[]> = {};
  for (const [country, d] of Object.entries(byCountry ?? {})) {
    if (d === digits) continue;
    groups[d] = [...(groups[d] ?? []), country];
  }
  return [
    digitsLevel(digits),
    ...Object.keys(groups)
      .map(Number)
      .sort((a, b) => a - b)
      .map((d) => `${groups[d].slice().sort().join(', ')} — ${digitsLevel(d)}`),
  ].join('; ');
}

/** Kogo pulpit liczy: opiekun, liczba pojazdów, region i horyzont. */
export function plannerScopeText(d: PlannerDashboardData): string {
  const parts: string[] = [];
  if (d.caretaker) {
    parts.push(
      `Opiekun FTL: ${d.caretaker}` +
        (d.vehicleCount !== null ? ` (pojazdy pod zlecenie: ${d.vehicleCount})` : ''),
    );
    const level = regionLevelText(d.regionPostalDigits, d.regionPostalDigitsByCountry);
    parts.push(
      d.regions.length > 0
        ? `mój region (${level}): ${d.regions.join(', ')}`
        : 'mój region: brak — żaden z moich pojazdów nie zwalnia się w znanym miejscu w horyzoncie',
    );
  }
  parts.push(`do obsadzenia: załadunek w ciągu ${horizonText(d.horizonDays)}`);
  return parts.join(' · ');
}

const PlannerDashboardScreen = () => {
  const load = useCallback(() => fetchPlannerDashboard(), []);
  const { data, error, refresh } = useDashboard<PlannerDashboardData>(load);
  return (
    <Stack spacing={2} sx={{ p: 2 }} data-testid="ftl-planner-dashboard">
      <Title title={menuLabel(PLANNER_DASHBOARD_RESOURCE)} />
      {error ? <Alert severity="error">{error}</Alert> : null}
      {data && !data.caretaker ? <Alert severity="info">{NO_CARETAKER_NOTE}</Alert> : null}
      {data ? (
        <Typography variant="body2" color="text.secondary" data-testid="ftl-planner-scope">
          {plannerScopeText(data)}. {WORKING_VALUES_NOTE}
        </Typography>
      ) : null}
      <TileGroups tiles={data?.tiles ?? []} titles={PLANNER_GROUP_TITLES} loading={!data && !error} />
      {data?.computedAt ? <Footer computedAt={data.computedAt} onRefresh={refresh} /> : null}
    </Stack>
  );
};

export const TeamLeaderDashboard = () => (
  <Gate
    feature={FEATURE_TEAM_LEADER_DASHBOARD}
    permission="FTL_ASSIGN"
    resource={TEAM_LEADER_DASHBOARD_RESOURCE}
    offNote={TEAM_LEADER_FEATURE_OFF_NOTE}
    noPermissionNote={TEAM_LEADER_NO_PERMISSION_NOTE}
  >
    <TeamLeaderDashboardScreen />
  </Gate>
);

const TeamLeaderDashboardScreen = () => {
  const load = useCallback(() => fetchTeamLeaderDashboard(), []);
  const { data, error, refresh } = useDashboard<TeamLeaderDashboardData>(load);
  // Bilans floty, postęp planistów, deficyt i nadwyżka (24.10.2026, S-24.10-08).
  const balanceOn = useFeature(FEATURE_TEAM_LEADER_BALANCE);
  return (
    <Stack spacing={2} sx={{ p: 2 }} data-testid="ftl-team-leader-dashboard">
      <Title title={menuLabel(TEAM_LEADER_DASHBOARD_RESOURCE)} />
      {error ? <Alert severity="error">{error}</Alert> : null}
      {data ? (
        <Typography variant="body2" color="text.secondary">
          Cała flota · do obsadzenia: załadunek w ciągu {horizonText(data.horizonDays)}. {HORIZON_WORKING_VALUE_NOTE}
        </Typography>
      ) : null}
      <TileGroups tiles={data?.tiles ?? []} titles={GROUP_TITLES} loading={!data && !error} />
      {balanceOn ? <TeamLeaderBalance /> : null}
      {data?.computedAt ? <Footer computedAt={data.computedAt} onRefresh={refresh} /> : null}
    </Stack>
  );
};
